import { AuthorizationProvider, useAuthentication } from '@autone/auth';
import { AutoneTranslation } from '@autone/translations';
import i18nAutone from '@autone/translations/production';
import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type ComponentProps, type FC, type PropsWithChildren } from 'react';
import { HelmetProvider } from 'react-helmet-async';

import { CollapseDrawerProvider } from '../../contexts/CollapseDrawerContext';
import { AuthGuard } from '../../guards';
import useLaunchDarkly from '../../hooks/useLaunchDarkly';
import ThemeConfig from '../../theme';
import { type ModuleName } from '../../types';
import NotistackProvider from '../notistack-provider/NotistackProvider';
import PosthogProvider from '../posthog-provider/PosthogProvider';

const STABLE_EMPTY_ARRAY: string[] = [];

type AuthenticatedAppProvidersProps = PropsWithChildren<{
  appWrapper: FC<PropsWithChildren<{ module: ModuleName }>>;
  editPermissions?: string[] | null;
  module: ModuleName;
  permissionGroups: string[];
  permissions?: {
    admin: string[];
    read?: string[];
    edit: string[];
  };
  translationNamespace: ComponentProps<typeof AutoneTranslation>['namespace'];
}>;

export const AuthenticatedAppProviders: FC<AuthenticatedAppProvidersProps> = ({
  appWrapper: AppWrapper,
  children,
  editPermissions,
  module,
  permissionGroups,
  permissions,
  translationNamespace,
}) => {
  const { user } = useAuthentication();
  const { ldHash, ldContext } = useLaunchDarkly();

  return (
    <AutoneTranslation
      namespace={translationNamespace}
      i18n={i18nAutone}
      customerId={user.customerId}
    >
      <AuthorizationProvider
        editPermissions={
          editPermissions || permissions?.edit || STABLE_EMPTY_ARRAY
        }
        adminPermissions={permissions?.admin}
        currentModulePermissions={permissionGroups}
      >
        <PosthogProvider>
          <HelmetProvider>
            <ThemeConfig>
              <CollapseDrawerProvider>
                <NotistackProvider>
                  <LDProvider
                    clientSideID={import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID!}
                    context={ldContext}
                    options={{ hash: ldHash }}
                    deferInitialization
                  >
                    <AuthGuard>
                      <AppWrapper module={module}>{children}</AppWrapper>
                    </AuthGuard>
                  </LDProvider>
                </NotistackProvider>
              </CollapseDrawerProvider>
            </ThemeConfig>
          </HelmetProvider>
        </PosthogProvider>
      </AuthorizationProvider>
    </AutoneTranslation>
  );
};
